import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const DashCard = ({ title, children }) => (
  <section className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-5">
    <h3 className="text-lg font-semibold mb-3">{title}</h3>
    <div className="flex flex-wrap gap-3">{children}</div>
  </section>
);

const Btn = ({ to, color = 'indigo', children, badge }) => {
  const colors = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    yellow: 'bg-yellow-500 hover:bg-yellow-600',
    purple: 'bg-purple-600 hover:bg-purple-700',
    red: 'bg-red-700 hover:bg-red-800',
    gray: 'bg-gray-600 hover:bg-gray-700',
  };
  return (
    <Link
      to={to}
      className={`relative inline-flex items-center text-white py-2 px-4 rounded-md ${colors[color]} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/40`}
    >
      <span>{children}</span>
      {badge > 0 && (
        <span
          className="ml-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs w-5 h-5"
          aria-label={`${badge} pending`}
          title={`${badge} pending`}
        >
          {badge}
        </span>
      )}
    </Link>
  );
};

const Dashboard = () => {
  const [pendingApprovals, setPendingApprovals] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/intakes?status=pending');
        const data = res.data;
        const count = Array.isArray(data) ? data.length : (typeof data?.count === 'number' ? data.count : 0);
        if (!cancelled) setPendingApprovals(count);
      } catch (e) {
        // fail silently; keep 0
        console.error('Failed to load pending approvals', e?.response?.data || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="container mx-auto p-6">
      <header className="mb-6"><h1 className="text-3xl font-semibold">Student &amp; Sales Dashboard</h1><p className="text-gray-500 mt-1">Choose the operational area for your work.</p></header>
      <section aria-labelledby="student-management" className="mb-8 border-l-4 border-blue-500 pl-4">
        <h2 id="student-management" className="text-2xl font-semibold mb-4">Salankaraa: Student Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <DashCard title="Students & Registration">
            <Btn to="/students/add" color="green">Add Student</Btn><Btn to="/students/list" color="blue">Student List</Btn><Btn to="/students/Approve" color="blue" badge={pendingApprovals}>Approve Student</Btn>
          </DashCard>
          <DashCard title="Student Fees & Payments">
            <Btn to="/payments/add" color="yellow">Add Payment</Btn><Btn to="/payments/manage" color="red">Manage Payment</Btn><Btn to="/reports/payments" color="purple">Daily Payment Report</Btn><Btn to="/reports/studentPayment" color="indigo">Student Payment</Btn>
          </DashCard>
          <DashCard title="Courses & Events">
            <Btn to="/mentors" color="indigo">Mentors &amp; Welcome Kits</Btn>
            <Btn to="/courses/list" color="blue">Course List</Btn><Btn to="/courses/add" color="green">Add Course</Btn><Btn to="/events/list" color="blue">Event List</Btn><Btn to="/events/add" color="green">Add Event</Btn><Btn to="/reports/event-enrollment" color="purple">Event Enrollments</Btn>
          </DashCard>
          <DashCard title="Student Reports & Management">
            <Btn to="/reports/mentors" color="purple">Mentor Report</Btn>
            <Btn to="/reports/student-anniversaries" color="purple">Anniversaries &amp; Fee Review</Btn>
            <Btn to="/reports/monthly" color="indigo">Monthly Fee Report</Btn><Btn to="/reports/monthlyLate" color="red">Late Report</Btn><Btn to="/reports/Yearly" color="gray">Yearly Report</Btn><Btn to="/reports/new-admissions" color="green">New Admission Report</Btn><Btn to="/reports/management" color="indigo">Management Dashboard</Btn>
          </DashCard>
        </div>
      </section>
      <section aria-labelledby="sales-management" className="border-l-4 border-emerald-500 pl-4">
        <h2 id="sales-management" className="text-2xl font-semibold mb-2">COG / The Bengal Craft / Ghoradum: Customer &amp; Sales Management</h2>
        <p className="text-sm text-gray-500 mb-4">Ghoradum is a commercial product line. Invoice organization values remain COG and The Bengal Craft.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <DashCard title="Invoices & Customers"><Btn to="/payments/running" color="green">Create Invoice</Btn><Btn to="/reports/running-sales" color="indigo">Running Sales Report</Btn><p className="text-xs text-gray-500">View and edit invoices through the existing report actions.</p></DashCard>
          <DashCard title="Delivery"><Btn to="/reports/running-sales#delivery-management" color="blue">Delivery Management</Btn><Btn to="/deliveries/bulk" color="indigo">Bulk Delivery</Btn></DashCard>
          <DashCard title="Products"><Btn to="/products" color="green">Product Master</Btn><Btn to="/products/mapping" color="purple">Product Mapping</Btn></DashCard>
          <DashCard title="Sales Reporting & Payments"><Btn to="/reports/smart" color="indigo">Smart Reporting</Btn><Btn to="/payments/running/manage" color="purple">Manage Invoices &amp; Payments</Btn></DashCard>
        </div>
        <div className="mt-5 rounded-xl border border-indigo-200 bg-indigo-50 p-5">
          <h3 className="text-lg font-semibold mb-2">Order Management &amp; Production Priorities</h3>
          <p className="text-sm text-gray-600 mb-3">Open orders, dispatch priorities, pending product demand and customer follow-up.</p>
          <Btn to="/reports/order-management" color="indigo">Order Management</Btn>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;